import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Pages } from '../../../Pages'
import Button from '../../common/form/inputs/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'
import {
    jsCapture,
    jsIdentify,
    setJsUserEmail,
    setJsUserId,
} from '../../../jobseeker/jobseekerPosthog'
import jobSeekerApi from '../../../api/jobSeeker/JobSeekerApi'
import LinkedInUrlHelp from '../LinkedInUrlHelp'
import authApi from '@/api/auth/AuthApi'
import warmupApi from '../../../api/warmup/WarmupApi'
import { executeRecaptcha } from '../../../utils/recaptcha'
import { buildTargetWithReturnPath } from '../utils/loginTarget'

type FormState = {
    name: string
    email: string
    phone: string
    linkedinUrl: string
    acceptedTerms: boolean
}

type TextField = Exclude<keyof FormState, 'acceptedTerms'>

type FormErrors = Partial<Record<keyof FormState, string>>

type CandidateSignUpFormProps = {
    mode?: 'page' | 'embedded'
    className?: string
    formClassName?: string
    flashTrigger?: number
}

export default function CandidateSignUpForm({
    mode = 'page',
    className = '',
    formClassName = '',
    flashTrigger = 0,
}: CandidateSignUpFormProps = {}): React.JSX.Element {
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const isEmbedded = mode === 'embedded'
    const prefillName = useMemo(
        () =>
            (
                searchParams.get('cand_name') ??
                searchParams.get('name') ??
                ''
            ).trim(),
        [searchParams],
    )
    const [formState, setFormState] = useState<FormState>({
        name: prefillName,
        email: (searchParams.get('cand_email') ?? '').trim(),
        phone: (searchParams.get('cand_phone') ?? '').trim(),
        linkedinUrl: (searchParams.get('cand_li_url') ?? '').trim(),
        acceptedTerms: false,
    })
    const normalizedReferralCode = useMemo(() => {
        const rawCode = searchParams.get('ref') ?? ''
        const trimmed = rawCode.trim()
        return trimmed ? trimmed.toUpperCase() : undefined
    }, [searchParams])
    const [errors, setErrors] = useState<FormErrors>({})
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [checkingSession, setCheckingSession] = useState(!isEmbedded)
    const [isFlashing, setIsFlashing] = useState(false)

    useEffect(() => {
        if (!isEmbedded) {
            jsCapture('js_signup_viewed')
        }
    }, [isEmbedded])

    useEffect(() => {
        let warmupTriggered = false
        let cancelled = false

        const triggerWarmup = async () => {
            if (warmupTriggered) return
            warmupTriggered = true
            try {
                const token = await executeRecaptcha('warmup')
                if (cancelled) return
                const finalToken = token ?? 'local-warmup-token'
                await warmupApi.warmup(finalToken)
            } catch (error) {
                console.warn('Failed to warm up system', error)
            }
        }

        triggerWarmup()

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (isEmbedded) {
            return
        }

        let active = true
        const checkSession = async () => {
            try {
                const res = await authApi.getMeDetails()
                if (!active) return
                if (res.user) {
                    navigate(Pages.CandidateInterview, { replace: true })
                }
            } catch {
                // ignore unauthenticated responses
            } finally {
                if (active) {
                    setCheckingSession(false)
                }
            }
        }

        checkSession()

        return () => {
            active = false
        }
    }, [isEmbedded, navigate])

    useEffect(() => {
        if (!flashTrigger) {
            return
        }

        setIsFlashing(true)
        const timeout = window.setTimeout(() => {
            setIsFlashing(false)
        }, 1200)

        return () => window.clearTimeout(timeout)
    }, [flashTrigger])

    const handleChange =
        (field: TextField) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setFormState((prev) => ({ ...prev, [field]: value }))
            setSubmitError(null)
            if (errors[field]) {
                setErrors((prev) => {
                    const next = { ...prev }
                    delete next[field]
                    return next
                })
            }
        }

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const checked = event.target.checked
        setFormState((prev) => ({ ...prev, acceptedTerms: checked }))
        setSubmitError(null)
        if (errors.acceptedTerms) {
            setErrors((prev) => {
                const next = { ...prev }
                delete next.acceptedTerms
                return next
            })
        }
    }

    const validate = (
        name: string,
        email: string,
        phone: string,
        linkedinUrl: string,
        acceptedTerms: boolean,
    ): FormErrors => {
        const nextErrors: FormErrors = {}

        if (!name) {
            nextErrors.name = 'Name is required.'
        }

        if (!email) {
            nextErrors.email = 'Email is required.'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            nextErrors.email = 'Enter a valid email address.'
        }

        if (!phone) {
            nextErrors.phone = 'Phone number is required.'
        }

        if (!linkedinUrl) {
            nextErrors.linkedinUrl = 'LinkedIn URL is required.'
        } else if (!linkedinUrl.toLowerCase().includes('linkedin.com/in')) {
            nextErrors.linkedinUrl =
                'Use your profile link that includes linkedin.com/in.'
        }

        if (!acceptedTerms) {
            nextErrors.acceptedTerms =
                'You must agree to the Terms and Conditions.'
        }

        return nextErrors
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const trimmedName = formState.name.trim()
        const trimmedEmail = formState.email.trim()
        const trimmedPhone = formState.phone.trim()
        const trimmedLinkedIn = formState.linkedinUrl.trim()
        const acceptedTerms = formState.acceptedTerms
        const fieldsPresent = [
            trimmedName ? 'name' : null,
            trimmedEmail ? 'email' : null,
            trimmedPhone ? 'phone' : null,
            trimmedLinkedIn ? 'linkedinUrl' : null,
            acceptedTerms ? 'acceptedTerms' : null,
        ].filter(Boolean) as string[]
        jsCapture('js_signup_submitted', {
            fields_present: fieldsPresent,
            referral_code: normalizedReferralCode,
        })

        const validationErrors = validate(
            trimmedName,
            trimmedEmail,
            trimmedPhone,
            trimmedLinkedIn,
            acceptedTerms,
        )
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }

        setFormState({
            name: trimmedName,
            email: trimmedEmail,
            phone: trimmedPhone,
            linkedinUrl: trimmedLinkedIn,
            acceptedTerms,
        })
        setErrors({})
        setSubmitError(null)
        setIsSubmitting(true)

        try {
            const response = await jobSeekerApi.submitSignup({
                name: trimmedName,
                email: trimmedEmail,
                phone: trimmedPhone,
                linkedinUrl: trimmedLinkedIn,
                acceptedTerms,
                source: 'candidate-sign-up',
                referralCode: normalizedReferralCode,
            })

            if (response.requiresLogin) {
                jsCapture('js_signup_login_required', {
                    email: trimmedEmail,
                    referral_code: normalizedReferralCode,
                })
                const target = buildTargetWithReturnPath(
                    Pages.CandidateInterview,
                    `${location.pathname}${location.search}`,
                )
                navigate(
                    `${Pages.CandidateLogin}?email=${encodeURIComponent(
                        trimmedEmail,
                    )}&step=choose-method&target=${encodeURIComponent(target)}`,
                    { replace: true },
                )
                return
            }

            const userId =
                typeof response.userId === 'string'
                    ? response.userId.trim()
                    : ''

            if (!userId) {
                throw new Error('Signup succeeded but no user id was returned.')
            }

            jsIdentify(userId, { email: trimmedEmail })
            setJsUserId(userId)
            setJsUserEmail(trimmedEmail)
            jsCapture('js_signup_succeeded', {
                user_id: userId,
                created_new_user: response.createdNewUser,
                referral_code: normalizedReferralCode,
            })

            navigate(Pages.CandidateInterview)
        } catch (error) {
            console.error('Failed to submit candidate sign up', error)
            const errorCode = 'request_failed'
            const errorMessage =
                error instanceof Error
                    ? error.message.slice(0, 120)
                    : 'request_failed'

            jsCapture('js_signup_failed', {
                error_code: errorCode,
                error_message: errorMessage,
                referral_code: normalizedReferralCode,
            })
            const message =
                error instanceof Error
                    ? error.message
                    : 'Unable to submit your details right now.'
            setSubmitError(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const formClasses = [
        'space-y-6 rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8',
        formClassName,
    ]
        .filter(Boolean)
        .join(' ')

    const containerClasses = [
        className,
        'rounded-2xl transition duration-500',
        isFlashing
            ? 'ring-2 ring-offset-2 ring-primary/50 shadow-xl shadow-primary/20'
            : '',
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div className={containerClasses}>
            <form className={formClasses} onSubmit={handleSubmit} noValidate>
                <div className="space-y-2">
                    <label
                        htmlFor="candidate-name"
                        className="text-sm font-semibold text-foreground"
                    >
                        Name
                    </label>
                    <input
                        id="candidate-name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={formState.name}
                        onChange={handleChange('name')}
                        className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="Jane Smith"
                        required
                    />
                    {errors.name && (
                        <p className="text-sm text-destructive">
                            {errors.name}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <label
                        htmlFor="candidate-email"
                        className="text-sm font-semibold text-foreground"
                    >
                        Email
                    </label>
                    <input
                        id="candidate-email"
                        name="email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={formState.email}
                        onChange={handleChange('email')}
                        className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="you@example.com"
                        required
                    />
                    {errors.email && (
                        <p className="text-sm text-destructive">
                            {errors.email}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <label
                        htmlFor="candidate-phone"
                        className="text-sm font-semibold text-foreground"
                    >
                        Phone
                    </label>
                    <input
                        id="candidate-phone"
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={formState.phone}
                        onChange={handleChange('phone')}
                        className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="123 456 7890"
                        required
                    />
                    <p className="text-sm text-muted-foreground">
                        We’ll send your job matches via WhatsApp.
                    </p>
                    {errors.phone && (
                        <p className="text-sm text-destructive">
                            {errors.phone}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <label
                        htmlFor="candidate-linkedin"
                        className="text-sm font-semibold text-foreground"
                    >
                        LinkedIn URL
                    </label>
                    <input
                        id="candidate-linkedin"
                        name="linkedinUrl"
                        type="url"
                        inputMode="url"
                        value={formState.linkedinUrl}
                        onChange={handleChange('linkedinUrl')}
                        className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                        placeholder="https://www.linkedin.com/in/your-profile"
                        required
                    />
                    {errors.linkedinUrl && (
                        <p className="text-sm text-destructive">
                            {errors.linkedinUrl}
                        </p>
                    )}
                    <LinkedInUrlHelp />
                </div>

                <div className="space-y-2">
                    <label
                        htmlFor="candidate-terms"
                        className="flex items-start gap-3 text-sm text-foreground"
                    >
                        <input
                            id="candidate-terms"
                            name="acceptedTerms"
                            type="checkbox"
                            checked={formState.acceptedTerms}
                            onChange={handleCheckboxChange}
                            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                            required
                        />
                        <span className="text-foreground">
                            I agree to the{' '}
                            <a
                                href="https://contxt.network/privacy-policy"
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary underline"
                            >
                                Terms and Conditions
                            </a>
                        </span>
                    </label>
                    {errors.acceptedTerms && (
                        <p className="text-sm text-destructive">
                            {errors.acceptedTerms}
                        </p>
                    )}
                </div>

                <Button
                    type="submit"
                    style="primary"
                    classes="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base md:text-lg font-medium rounded-lg bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90 hover:shadow-primary/50 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    enabled={!isSubmitting && !checkingSession}
                >
                    {!isSubmitting && (
                        <FontAwesomeIcon icon={faPhone} className="mr-2" />
                    )}
                    {isSubmitting ? 'Submitting…' : 'Speak to Kit'}
                </Button>
                {submitError && (
                    <p className="text-center text-sm text-destructive">
                        {submitError}
                    </p>
                )}
            </form>
        </div>
    )
}
